import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaMaskingDependencyUtility from '@sparta.global.utilities/sparta-masking-dependency-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';

/* eslint-disable no-param-reassign */
/* eslint-disable brace-style */
/**
 * Namespace for the Sparta Chip Utility.
 * @constant {string}
 */
const utilityNamespace = 'spartaMaskingUtility';

/**
 * Collection of namespaced event strings.
 * @constant {object}
 */
const EVENTS = {
  addTextMaskingInitEvent: `${utilityNamespace}.Mask_Add_Text_Masking`,
  maskTextValueEvent: `${utilityNamespace}.Mask_Text_Value`,
  maskTextValueToggleEvent: `${utilityNamespace}.Mask_Text_Value_Toggle`,
  maskTextValueSetMaskedEvent: `${utilityNamespace}.Mask_Text_Value_Set_Masked`,
  maskTextValueSetUnmaskedEvent: `${utilityNamespace}.Mask_Text_Value_Set_Unmasked`,
  maskTextValueTimeoutEvent: `${utilityNamespace}.Mask_Text_Value_Timeout`
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        SPARTA Masking Utility
 * @file        sparta-masking-utility.js
 * @description Provides GIS masking logic for inputs and text values
 * @classdesc   Provides GIS masking logic for inputs and text values
 * @global      Creates a a global GISMasking object
 * @author      Kerry Boyce
 * @copyright   © 2016 Bank of America Corporation. All rights reserved.
 *
 * The GIS Masking library is used to create, present, and control the masking functionality
 * required by GIS to protect the privacy of customer sensitive personal information (SPI).  Any
 * HTML text inputs or text elements that contain SPI, must be tagged with the appropriate
 * "gis-mask" class to indicate that it needs masking and a "data-field-type" attribute
 * (described below) to identify any data-specific masking rules that need to be triggered.
 *
 * The currently supported "data-field-type" attribute values are:
 *     **no value** - will apply the default mask, which will mask all characters in the field
 *     email        - the mask for email addresses, which masks only the "local" part of the
 *                    email (i.e., the part before the "@").  How the local is mask is based on
 *                    it's length:
 *                       - for email addresses with local values fewer than 3 characters, all
 *                         characters in the local are masked
 *                       - for email addresses with local values with 3 or more characters, all
 *                         characters but the first and last ones in the local are masked
 *     phone        - the mask for phone numbers, which masks the first 6 digits in the phone
 *                    number
 *     account      - the mask for Bank of America account numbers, which masks all but the last
 *                    4 digits in the field
 *     date         - the mask for dates, which masks all alphanumeric characters in the field
 *     ssn          - the mask for social security numbers (SSN) and employer identification
 *                    numbers (EIN), which masks all alphanumeric characters in the field
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

class SpartaMaskingUtility {
  init(settings = {}) {
    const spaParamsLang = spartaStoreUtility.getData('jcrLang');
    const spaParams = spartaStoreUtility.getData('spaParams').global[spaParamsLang];
    const globalJs = spaParams.inputs;
    const {
      autoMask
    } = settings;
    const {
      widgetID = ''
    } = settings;
    const {
      globalStrings = globalJs
    } = settings;
    this.data.maskingStrings = globalStrings.masking;
    const maskingClassBase = this.data.globals.MASKING_CLASS_BASE;

    /* apply the masking functionality to all fields in the page or widget (if 'widgetID'
      is provided) tagged with the a class containing the value
      stored in "GISMasking.data.globals.MASKING_CLASS_BASE" */
    if (autoMask) {
      const maskingScope = document.getElementById(widgetID) || document;
      if (maskingScope instanceof Element || maskingScope instanceof Document) {
        const list = maskingScope.querySelectorAll(`.${maskingClassBase}`);
        if (list && list.length) {
          this.fn.core.addMasking(list);
        }
      }
    }
  }
  get data() {
    return data;
  }
  get fn() {
    return fn;
  }
  get EVENTS() {
    return EVENTS;
  }
}
const spartaMaskingUtility = new SpartaMaskingUtility();

// GISMasking.data - library-level variables
const data = spartaMaskingDependencyUtility.data;

// GISMasking.fn - library methods
const fn = {
  // "fn.helpers" methods - common "utility" functions that are used in multiple places in the library
  helpers: spartaMaskingDependencyUtility.fn.helpers,
  // "fn.maskingRules" methods - the class specific functions that hold the logic and data values specific to each unique masking rule
  maskingRules: spartaMaskingDependencyUtility.fn.maskingRules,
  // "fn.core" methods - the core functions in the library that set up the masked fields and activate the masking functionality, when triggered
  core: {
    /* "core.addMasking" method
     * "addMasking" takes a group of inputs element and adds the GISMasking functionality to them.
     * The method cycles through each element in the group and, if it does not already have a paired
     * "masking field", creates one and adds the event listeners required to support the appropriate
     * masking patterns.
     *
     * Parameters:
     *     $inputGroup - a jQuery reference to a group of input element require the GISMasking
     *     functionality
     *
     * Returns: -none-
     */
    addMasking(maskingGroup, settings = {}) {
      const maskingGroups = maskingGroup ? typeof maskingGroup.length !== 'undefined' ? Array.from(maskingGroup) : [maskingGroup] : [];
      if (maskingGroups.length) {
        // set the PAGE_LANGUAGE global, if it does not match the page language 
        const sPageLanguage = document.querySelector('html').getAttribute('lang') ? document.querySelector('html').getAttribute('lang').replace(/[^a-z]/gi, '') : 'enUS';
        if (spartaMaskingUtility.data.globals.PAGE_LANGUAGE !== sPageLanguage) {
          spartaMaskingUtility.data.globals.PAGE_LANGUAGE = sPageLanguage;
        }

        // add input and text masking
        spartaMaskingUtility.fn.core.addInputMasking(maskingGroups.filter(field => field.nodeName === 'INPUT'), settings);
        spartaMaskingUtility.fn.core.addTextMasking(maskingGroups.filter(field => field.nodeName !== 'INPUT'), settings);
        spartaEventsUtility.sub('FORMATTING_COMPLETE.spartaInputFormattingUtility', () => {
          spartaMaskingUtility.fn.core.addInputMasking(maskingGroups.filter(field => field.nodeName === 'INPUT'));
        }, true);
      }
    },
    /* "core.addInputMasking" method
     * "addInputMasking" takes a group of inputs element and adds the GISMasking functionality.
     * The method cycles through each element in the group and, if it does not already have a
     * paired "masking field", creates one and adds the event listeners required to support the
     * appropriate masking patterns.
     *
     * Parameters:
     *     $inputGroup - a jQuery reference to a group of input element require the GISMasking
     *     functionality
     *
     * Returns: -none-
     */
    addInputMasking(inputGroup, settings = {}) {
      // Make element lookup more robust
      let maskingItems = [];
      inputGroup.forEach(element => {
        // We've been given the masking toggle button directly
        if (element.classList.contains(spartaMaskingUtility.data.globals.MASKING_CLASS_BASE)) {
          maskingItems.push(element);
        }
        // We've been given some selector, so we need to search for any masking toggle buttons
        else {
          const findMaskingItems = [...element.getElementsByClassName(spartaMaskingUtility.data.globals.MASKING_CLASS_BASE)];
          findMaskingItems.forEach(maskingItem => {
            maskingItems.push(maskingItem);
          });
        }
      });
      maskingItems.forEach(element => {
        // check to see if there is already a "masking field" paired with the current input
        const inputField = element;
        const inputWrapper = inputField.closest('.spa-input');
        const inputHelpIcon = inputWrapper && inputWrapper.querySelector('.toggle-trigger') ? inputWrapper.querySelector('.toggle-trigger') : undefined;

        // Make sure input has legacy classes
        inputField.classList.add(spartaMaskingUtility.data.globals.MASKING_CLASS_ORIGINAL);

        // If no masking overlay, create it
        if (!inputField.classList.contains('spa-input-has-masking-overlay')) {
          // Add marker to input so this logic isn't run twice
          inputField.classList.add('spa-input-has-masking-overlay');

          // create wrapper container
          const wrapper = document.createElement('div');
          wrapper.classList.add('spa-input-mask-group');
          wrapper.innerHTML = `<div class="spa-input-mask-overlay">
            <span id="${inputField.id}-masked" class="spa-masked-input-value ${spartaMaskingUtility.data.globals.MASKING_CLASS_READY}"></span>
          </div>`.trim();

          // insert wrapper before el in the DOM tree
          inputField.parentNode.insertBefore(wrapper, inputField);

          // move el into wrapper
          wrapper.appendChild(inputField);

          // Check for help icon and move it for proper alignment with input
          const hasHelp = inputWrapper && inputWrapper.classList.contains('has-help');
          if (hasHelp && inputHelpIcon) {
            const helpIcon = inputWrapper.querySelector('.toggle-trigger-box');
            inputWrapper.querySelector('.spa-input-mask-group').appendChild(helpIcon);
          }

          // add an event listener to display the masking field, with the masked data value,
          // 0.2 seconds after the original field loses focus
          let inputMaskOverlay = inputField.previousElementSibling;
          inputMaskOverlay.addEventListener('click', () => {
            inputField.focus();
          });
          inputField.addEventListener('focusin', () => {
            inputMaskOverlay.classList.add('spa-hidden');
          });
          inputField.addEventListener('blur', () => {
            spartaMaskingUtility.fn.core.maskInputData(inputField);
          });
        }

        // immediately display the masking field, with the masked data value, to mask any "pre-filled" data that may be in the input
        spartaMaskingUtility.fn.core.maskInputData(inputField);
      });
    },
    /* "core.maskInputData" method
     * "maskInputData" applies the masking rules to the value of an input element and displays the
     * masked version in the associated "masking field", showing it in place of the original input.
     *
     * Parameters:
     *     $element - a jQuery reference to the input element that is being masked
     *     iDelay   - an integer representing the how long, in milliseconds, the method should wait
     *                before applying the mask
     *
     * Returns: -none-
     */
    maskInputData(element, iDelay = 0) {
      const sCurrVal = element.value.trim();

      // only apply the masking if the field is not empty and the value does not match the inputs
      // "placeholder" attribute, if one exists (in place to handle IE-specific placeholder workarounds)
      if (sCurrVal !== '' && sCurrVal !== element.getAttribute('placeholder')) {
        // retrieve the specific masking class for the input
        const sMaskingType = element.getAttribute('data-field-type');
        const inputMask = element.parentNode.querySelector('.spa-input-mask-overlay');
        const sInputMaskId = inputMask && inputMask.firstElementChild && inputMask.firstElementChild.id ? inputMask.firstElementChild.id : undefined;

        // Kill check
        if (!inputMask || !sInputMaskId) {
          console.warn('**WARNING** in sparta-masking-utility: inputMask does not exist or its firstElementChild has no Id.');
          return false;
        }

        // default the masking function to the basic mask, in case the "sMaskingType" does not match
        // one of the existing "GISMasking.fn.maskingRules" methods
        let sMaskingFunctionName = 'basic';
        if (Object.prototype.hasOwnProperty.call(spartaMaskingUtility.fn.maskingRules, sMaskingType)) {
          sMaskingFunctionName = sMaskingType;
        }

        // copy the current content of the input to the masking field and turn text into bullets
        inputMask.classList.remove('spa-hidden');
        inputMask.querySelector('.spa-masked-input-value').innerText = spartaMaskingUtility.fn.maskingRules[sMaskingFunctionName](sCurrVal);
      }
    },
    /* "core.addTextMasking" method
     * "addTextMasking" takes a group of inputs element and adds the GISMasking functionality to
     * them.  The method cycles through each element in the group and, if it does not already have a
     * paired "masking field", creates one and adds the event listeners required to support the
     * appropriate masking patterns.
     *
     * Parameters:
     *     $inputGroup - a jQuery reference to a group of input element require the GISMasking
     *     functionality
     *
     * Returns: -none-
     */
    addTextMasking(textGroup, settings = {}) {
      const options = Object.assign({
        state: undefined,
        timer: spartaMaskingUtility.data.globals.MASKED_DELAY_TIMER
      }, settings);
      const {
        state
      } = options;
      const {
        timer
      } = options;

      // Make element lookup more robust
      let maskingItems = [];
      textGroup.forEach(element => {
        // We've been given the masking toggle button directly
        if (element.classList.contains(spartaMaskingUtility.data.globals.MASKING_CLASS_BASE)) {
          maskingItems.push(element);
        }
        // We've been given some selector, so we need to search for any masking toggle buttons
        else {
          const findMaskingItems = [...element.getElementsByClassName(spartaMaskingUtility.data.globals.MASKING_CLASS_BASE)];
          findMaskingItems.forEach(maskingItem => {
            maskingItems.push(maskingItem);
          });
        }
      });

      // If we've been given something other than 'gis-mask' button element, lets look for it     
      maskingItems.forEach(element => {
        const triggerElm = spartaMaskingUtility.fn.core.getMaskingBtnElm(element);
        if (triggerElm) {
          // Get default state from helper or use default
          const maskedField = triggerElm.closest('.masked-element');
          const defaultState = state || maskedField && maskedField.hasAttribute('data-masked-state') ? triggerElm.closest('.masked-element').getAttribute('data-masked-state') : 'masked';
          const disableToggleListener = triggerElm.hasAttribute('data-enable-toggle') ? triggerElm.getAttribute('data-enable-toggle').toString() : 'false';
          const hasBeenInitialized = !!triggerElm.getAttribute('data-masking-init');
          if (!hasBeenInitialized) {
            // Mask visible values
            spartaMaskingUtility.fn.core.maskTextValue(triggerElm, {
              state: defaultState,
              timer
            });

            // Attach click event to show/hide button
            if (disableToggleListener === 'false') {
              triggerElm.addEventListener('click', () => {
                spartaMaskingUtility.fn.core.maskTextValueToggle(triggerElm, {
                  revertState: 'masked',
                  timer
                });
              });
            }

            // Publish events
            spartaEventsUtility.pub(`${EVENTS.addTextMaskingInitEvent}:${maskedField.id}`, {
              element: maskedField,
              control: triggerElm,
              maskingStrings: spartaMaskingUtility.data.maskingStrings,
              state: defaultState,
              timer
            });

            // add initialization data attribute
            triggerElm.setAttribute('data-masking-init', true);
          }
        } else {
          console.warn('No `gis-mask` element found in provided element', triggerElm);
        }
      });
    },
    /**
     * Check if the element we've been given is the show/hide toggle button or something else
     * @param {Object} element Source element that we will search for
     */
    getMaskingBtnElm(element) {
      // We've been given the show/hide button element
      if (element.classList.contains(spartaMaskingUtility.data.globals.MASKING_CLASS_BASE)) {
        return element;
      }
      // Else, search the given element's children for the show/hide button
      else if (element.querySelector(`.${spartaMaskingUtility.data.globals.MASKING_CLASS_BASE}`)) {
        return element.querySelector(`.${spartaMaskingUtility.data.globals.MASKING_CLASS_BASE}`);
      }
    },
    /**
     * Builds a string for the show/hide ADA label based on show/hide state
     * @param {Object} element Masking show/hide button where we will alter the ADA logic
     * @param {String} state If text is masked or unmasked
     * @param {Number} timer Update the timer string to notify user when masking events happen
     */
    setMaskingBtnAda(element, state, timer) {
      const maskingStrings = spartaMaskingUtility.data.maskingStrings;
      const adaLabelValue = element.getAttribute('data-ada-label');
      let result = '';

      // Update label based on state
      if (state === 'masked') {
        result = `${maskingStrings.actions.show} ${adaLabelValue}`;
      } else {
        // Append instructions
        result = `${maskingStrings.actions.hide} ${adaLabelValue} ${maskingStrings.instructions.message}`;

        // Update timer
        if (timer !== spartaMaskingUtility.data.globals.MASKED_DELAY_TIMER) {
          result = result.replace(spartaMaskingUtility.data.globals.MASKED_DELAY_TIMER / 1000, timer / 1000);
        }
      }
      return result;
    },
    /**
     * Toggles the show/hide state of the masked element
     * @param {Object} element Show/hide button element
     * @param {String} state Determine which state the mask should take
     */
    maskTextValue(element, settings = {}) {
      const options = Object.assign({
        state: undefined,
        timer: spartaMaskingUtility.data.globals.MASKED_DELAY_TIMER
      }, settings);
      const {
        timer
      } = options;
      let {
        state
      } = options;
      const maskingStrings = spartaMaskingUtility.data.maskingStrings;

      // Masked value element
      const triggerElm = spartaMaskingUtility.fn.core.getMaskingBtnElm(element);
      const maskedField = triggerElm.closest('.masked-element');
      const maskedType = maskedField.getAttribute('data-field-type');
      const maskedElement = maskedField.querySelector('.masking-value');
      const maskedBtn = maskedField.querySelector('button');

      // Update the masking state
      const unmaskedValue = maskedElement.getAttribute('data-value-unmasked');
      const maskedValue = maskedElement.getAttribute('data-value-masked') ? maskedElement.getAttribute('data-value-masked') : spartaMaskingUtility.fn.maskingRules[maskedType](unmaskedValue);

      // If no state present check
      if (!state) {
        state = triggerElm.innerText.trim() === maskingStrings.actions.hide ? 'masked' : 'unmasked';
      }
      const hasMaskingBeenInitialized = !!triggerElm.getAttribute('data-masking-init');
      // is value visible and is already in the correct state before we initialize.
      const isValueInStatebeforeInitialization = maskedElement.innerText === maskedValue && !hasMaskingBeenInitialized;
      if (!isValueInStatebeforeInitialization || hasMaskingBeenInitialized) {
        // Toggle text, attributes, etc
        if (state === 'masked') {
          maskedBtn.innerText = maskingStrings.actions.show;
          maskedBtn.setAttribute('aria-label', spartaMaskingUtility.fn.core.setMaskingBtnAda(element, state, timer));
          maskedElement.innerText = maskedValue;
          maskedField.setAttribute('data-masked-state', 'masked');
        } else {
          maskedBtn.innerText = maskingStrings.actions.hide;
          maskedBtn.setAttribute('aria-label', spartaMaskingUtility.fn.core.setMaskingBtnAda(element, state, timer));
          maskedElement.innerText = unmaskedValue;
          maskedField.setAttribute('data-masked-state', 'unmasked');
        }
      }

      // Publish events
      spartaEventsUtility.pub(`${EVENTS.maskTextValueEvent}:${maskedField.id}`, {
        element: maskedField,
        control: triggerElm,
        maskingStrings,
        state
      });
    },
    /**
     * Toggles a masked element before reverting it aftera delay
     * @param {Object} element Show/hide button element
     * @param {Object} options Object that contains extra options to control how masking timeout controls
    */
    maskTextValueToggle(element, settings = {}) {
      const options = Object.assign({
        revertState: undefined,
        timer: spartaMaskingUtility.data.globals.MASKED_DELAY_TIMER
      }, settings);
      const maskedField = element.closest('.masked-element');
      const {
        timer
      } = options;
      const {
        revertState
      } = options;
      const triggerElm = spartaMaskingUtility.fn.core.getMaskingBtnElm(element);
      const maskingStrings = spartaMaskingUtility.data.maskingStrings;
      const setState = triggerElm.innerText.trim() === maskingStrings.actions.hide ? 'masked' : 'unmasked';
      spartaMaskingUtility.fn.core.maskTextValue(triggerElm, {
        state: setState,
        timer
      });
      spartaMaskingUtility.fn.core.maskTextValueTimeout(triggerElm, {
        state: revertState,
        timer
      });

      // Publish events
      spartaEventsUtility.pub(`${EVENTS.maskTextValueToggleEvent}:${maskedField.id}`, {
        element: maskedField,
        control: triggerElm,
        maskingStrings,
        state: setState,
        revertState,
        timer
      });
    },
    /**
     * Mask a text field and revert back after a delay
     * @param {Object} element Show/hide button element
     * @param {Object} options Object that contains extra options to control how masking timeout controls
     */
    maskTextValueSetMasked(element, settings = {}) {
      const options = Object.assign({
        timer: spartaMaskingUtility.data.globals.MASKED_DELAY_TIMER
      }, settings);
      const {
        timer
      } = options;
      const triggerElm = spartaMaskingUtility.fn.core.getMaskingBtnElm(element);
      const maskedField = element.closest('.masked-element');
      spartaMaskingUtility.fn.core.maskTextValue(triggerElm, {
        state: 'masked',
        timer
      });
      spartaMaskingUtility.fn.core.maskTextValueTimeout(triggerElm, {
        state: 'unmasked',
        timer
      });

      // Publish events
      spartaEventsUtility.pub(`${EVENTS.maskTextValueSetMaskedEvent}:${maskedField.id}`, {
        element: maskedField,
        control: triggerElm,
        maskingStrings: spartaMaskingUtility.data.maskingStrings,
        timer
      });
    },
    /**
     * Unmask a text field and revert back after a delay
     * @param {Object} element Show/hide button element
     * @param {Object} options Object that contains extra options to control how masking timeout controls
     */
    maskTextValueSetUnmasked(element, settings = {}) {
      const options = Object.assign({
        timer: spartaMaskingUtility.data.globals.MASKED_DELAY_TIMER
      }, settings);
      const {
        timer
      } = options;
      const triggerElm = spartaMaskingUtility.fn.core.getMaskingBtnElm(element);
      const maskedField = element.closest('.masked-element');
      spartaMaskingUtility.fn.core.maskTextValue(triggerElm, {
        state: 'unmasked',
        timer
      });
      spartaMaskingUtility.fn.core.maskTextValueTimeout(triggerElm, {
        state: 'masked',
        timer
      });

      // Publish events
      spartaEventsUtility.pub(`${EVENTS.maskTextValueSetUnmaskedEvent}:${maskedField.id}`, {
        element: maskedField,
        control: triggerElm,
        maskingStrings: spartaMaskingUtility.data.maskingStrings,
        timer
      });
    },
    /**
     * Set a timeout to revert masked state
     * @param {Object} element Show/hide button element
     * @param {Object} options Object that contains extra options to control how masking timeout controls
     */
    maskTextValueTimeout(element, settings = {}) {
      const options = Object.assign({
        state: undefined,
        timer: spartaMaskingUtility.data.globals.MASKED_DELAY_TIMER
      }, settings);
      const {
        timer
      } = options;
      const {
        state
      } = options;
      const triggerElm = spartaMaskingUtility.fn.core.getMaskingBtnElm(element);
      const maskedField = triggerElm.closest('.masked-element');
      const sRemaskId = `${spartaMaskingUtility.data.globals.AUTO_RE_MASK_TEXT_PREFIX}${maskedField.id}`;
      const maskingStrings = spartaMaskingUtility.data.maskingStrings;

      // State logic checks
      let setState = state;
      if (!state) {
        setState = triggerElm.innerText.trim() === maskingStrings.actions.hide ? 'masked' : 'unmasked';
      }

      // Clear existing timeout
      clearTimeout(spartaMaskingUtility.data.timeOuts[sRemaskId]);

      // Create a new timeout
      spartaMaskingUtility.data.timeOuts[sRemaskId] = window.setTimeout(() => {
        // Re-mask after timeout
        spartaMaskingUtility.fn.core.maskTextValue(triggerElm, {
          state: setState,
          timer
        });

        // Publish events
        spartaEventsUtility.pub(`${EVENTS.maskTextValueTimeoutEvent}:${maskedField.id}`, {
          element: maskedField,
          control: triggerElm,
          maskingStrings,
          state: setState,
          timer
        });
      }, timer);
    }
  }
};

export { spartaMaskingUtility as default };
